import type { TuningSettingKey, TuningSettingSupport, TuningValue } from '../contracts/settings';
import type { TuningSettingReceipt } from '../contracts/identity';
import { isTuningSettingValue } from '../contracts/settings';

type ReceiverLike = { track?: { kind: string }; jitterBufferTarget?: number | null; playoutDelayHint?: number | null };
type ReceiverSource = { getReceivers(): readonly ReceiverLike[] };
const keys: readonly TuningSettingKey[] = ['receiver.videoJitterBufferTargetMs', 'receiver.videoPlayoutDelayHintMs',
  'receiver.audioJitterBufferTargetMs', 'receiver.audioPlayoutDelayHintMs'];
type Binding = { receiver: ReceiverLike; property: 'jitterBufferTarget' | 'playoutDelayHint'; original: number | null | undefined; scale: number };

/** Trial hints are independent properties; null releases ownership back to the saved production receiver. */
export class GuestReceiverControlManager {
  private source: ReceiverSource | null = null;
  private readonly overrides = new Map<TuningSettingKey, Binding>();
  attach(source: ReceiverSource | null): void {
    if (source === this.source) return;
    this.restore(); this.overrides.clear(); this.source = source;
  }
  support(key: TuningSettingKey): TuningSettingSupport {
    const binding = this.resolve(key);
    return { mode: binding ? 'live' : 'unsupported', scope: 'peer', nativeApi: binding ? 'supported' : 'unsupported',
      min: 0, max: 1_000, reason: binding ? undefined : 'Native receiver hint is unavailable' };
  }
  apply(key: TuningSettingKey, requested: TuningValue): TuningSettingReceipt {
    if (!keys.includes(key) || !isTuningSettingValue(key, requested)) return { key, requested, status: 'failed', reason: 'Invalid receiver hint' };
    const binding = this.overrides.get(key) ?? this.resolve(key);
    if (!binding) return { key, requested, status: 'unsupported', reason: 'Native receiver hint is unavailable' };
    try {
      if (requested === null) {
        return this.restoreBinding(key, binding);
      } else {
        this.overrides.set(key, binding);
        binding.receiver[binding.property] = (requested as number) / binding.scale;
      }
      const readback = binding.receiver[binding.property];
      if (readback !== null && (typeof readback !== 'number' || !Number.isFinite(readback))) {
        return { key, requested, status: 'failed', reason: 'Native hint readback is unavailable' };
      }
      const value = readback === null ? null : readback * binding.scale;
      return { key, requested, accepted: requested, readback: value,
        status: requested !== null && value !== requested ? 'clamped' : 'applied' };
    } catch (error) { return { key, requested, status: 'failed', reason: String(error).slice(0, 2_048) || 'Native hint rejected' }; }
  }
  restore(): TuningSettingReceipt[] {
    return [...this.overrides].map(([key, binding]) => this.restoreBinding(key, binding));
  }
  dispose(): void { this.restore(); this.overrides.clear(); this.source = null; }
  owns(key: TuningSettingKey): boolean { return this.overrides.has(key); }
  private restoreBinding(key: TuningSettingKey, binding: Binding): TuningSettingReceipt {
    try {
      binding.receiver[binding.property] = binding.original;
      const nativeReadback = binding.receiver[binding.property];
      const readback = nativeReadback === null ? null : typeof nativeReadback === 'number' && Number.isFinite(nativeReadback)
        ? nativeReadback * binding.scale : undefined;
      if (nativeReadback !== binding.original) return { key, requested: null, readback, status: 'failed',
        reason: 'Native receiver did not restore its production hint' };
      this.overrides.delete(key);
      return { key, requested: null, accepted: null, readback, status: 'applied' };
    } catch (error) {
      return { key, requested: null, status: 'failed', reason: String(error).slice(0, 2_048) || 'Native hint restoration rejected' };
    }
  }
  private resolve(key: TuningSettingKey): Binding | undefined {
    if (!keys.includes(key) || !this.source) return undefined;
    const kind = key.includes('.audio') ? 'audio' : 'video';
    const property = key.endsWith('PlayoutDelayHintMs') ? 'playoutDelayHint' : 'jitterBufferTarget';
    try {
      const receiver = this.source.getReceivers().find((item) => item.track?.kind === kind);
      if (!receiver || !(property in receiver)) return undefined;
      return { receiver, property, original: receiver[property], scale: property === 'playoutDelayHint' ? 1_000 : 1 };
    } catch { return undefined; }
  }
}
